import React from "react";
import { Link } from "react-router-dom";
import { Lightbulb, BookOpen, FileSearch, ScrollText, BadgeCheck, ArrowRight } from "lucide-react";

const STEPS = [
  { icon: Lightbulb, title: "Idea or thesis", desc: "Bring a raw idea, a class project or an unfinished thesis." },
  { icon: BookOpen, title: "Course-led learning", desc: "Cohort lessons on writing, methodology, ethics and journal targeting." },
  { icon: FileSearch, title: "Mentor-guided drafting", desc: "Domain expert turns the work into a publishable manuscript." },
  { icon: ScrollText, title: "Submission & review", desc: "Q1-targeting, response letters, ethical plagiarism review." },
  { icon: BadgeCheck, title: "Published paper", desc: "Indexed, citable, portfolio-ready scholarship." },
];

/**
 * "Course → Paper" roadmap. Visualises the conversion of a class project /
 * thesis into a published paper. Tightly wired to onboarding CTAs.
 */
export default function CourseToPaperRoadmap() {
  return (
    <section className="bg-white border-y border-kb-divider" data-testid="course-to-paper-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple mb-4">
              COURSE → PAPER
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-kb-ink leading-[1.08]">
              From a class project to a <em className="italic text-kb-purple">published paper</em>.
            </h2>
            <p className="mt-5 text-kb-ink-muted max-w-2xl">
              A five-step conversion: learn the craft inside our LMS, then route the very same
              project through our mentor-led publication workflow. One ecosystem, no hand-offs.
            </p>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end gap-3">
            <Link to="/project-to-paper" className="kb-btn-outline" data-testid="roadmap-cta-learn">
              How it works
            </Link>
            <Link to="/onboarding/role" className="kb-btn-primary" data-testid="roadmap-cta-start">
              Start your paper <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Roadmap rail */}
        <div className="relative">
          <div className="hidden md:block absolute left-0 right-0 top-[40px] h-px bg-gradient-to-r from-transparent via-kb-purple/40 to-transparent" />
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative kb-tilt kb-card p-5" data-testid={`roadmap-step-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-full bg-kb-subtle border border-kb-divider flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-kb-purple" strokeWidth={1.6} />
                  </div>
                  <span className="kb-mono text-[10px] tracking-[0.2em] text-kb-ink-light">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="font-serif text-lg text-kb-ink mt-4 leading-tight">{s.title}</div>
                <div className="text-xs text-kb-ink-muted mt-1.5 leading-relaxed">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
